/**
 * Minimal RFC 4180 CSV parsing — quoted fields, embedded commas, doubled-quote
 * escaping, and CRLF or LF line endings. No dependency pulled in for it: the import
 * pane is the only caller, and Buildium/spreadsheet exports do not need anything
 * more exotic than this handles.
 */
export interface ParsedCsv {
  headers: string[];
  rows: string[][];
}

export function parseCsv(text: string): ParsedCsv {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let inQuotes = false;
  let sawAnyField = false;

  for (let i = 0; i < text.length; i++) {
    const char = text[i];

    if (inQuotes) {
      if (char === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += char;
      }
      continue;
    }

    if (char === '"') {
      inQuotes = true;
      sawAnyField = true;
    } else if (char === ',') {
      row.push(field);
      field = '';
      sawAnyField = true;
    } else if (char === '\n') {
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      sawAnyField = false;
    } else if (char === '\r') {
      // Swallowed; the following '\n' (or end of input) closes the row.
      continue;
    } else {
      field += char;
      sawAnyField = true;
    }
  }
  if (sawAnyField || field !== '') {
    row.push(field);
    rows.push(row);
  }

  const [headerRow, ...dataRows] = rows;
  const headers = (headerRow ?? []).map((header) => header.trim());
  // Drop rows that are entirely blank — a trailing newline in the file is the
  // common cause, and an all-empty row would otherwise fail every required field.
  const nonEmpty = dataRows.filter((candidate) => candidate.some((cell) => cell.trim() !== ''));

  return { headers, rows: nonEmpty };
}
